import * as d3 from "d3";
import ActiveRegion from "./Auxiliar/ActiveRegion";
import appendEventFunctions from "./Auxiliar/appendEventFunctions";

export default class FocusManager {
    // Public:
    constructor(cParams) {
        this.saveParams(cParams);
        this.defineProperties();
        this.cloneInputNodes();
        this.setInitialState();
    }

    compute() {
        this.attachContractFocusFunction();
        this.attachExpandFocusFunction();
    }

    attachContractFocusFunction() {
        let func = e => this.contractFocus(e);
        this.attachFunctionToCircles(func);
        this.attachFunctionToLabels(func);
    }

    attachExpandFocusFunction() {
        let func = e => {
            if (e.target.localName !== "circle") this.expandFocus();
        };
        const elementId = this._elementId;
        let chart = document.getElementById("" + elementId);
        chart.onclick = appendEventFunctions([chart.onclick, func]);
    }

    //Private:
    saveParams(cParams) {
        this._activeDataManager = cParams.activeDataManager;
        this._inputNodes = cParams.inputNodes;
        this._inputLinks = cParams.inputLinks;
        this._clusters = cParams.clusters;
        this._chart = cParams.chart;
        this._elementId = cParams.chart._elementId;
    }

    defineProperties() {
        this.defineState();
        this.definePreviousState();
    }

    defineState() {
        let setFunc = s => this.setState(s);
        let getFunc = () => this.getState();
        Object.defineProperty(this, "state", {
            get: getFunc,
            set: setFunc,
        });
    }

    definePreviousState() {
        let setFunc = s => this.setPreviousState(s);
        let getFunc = () => this.getPreviousState();
        Object.defineProperty(this, "state0", {
            get: getFunc,
            set: setFunc,
        });
    }

    cloneInputNodes() {
        let inputNodes = this._inputNodes;
        let clonedNodes = [];
        for (let i = 0; i < inputNodes.length; i++) {
            const node = inputNodes[i];
            let clonedNode = this.clone(node);
            clonedNodes.push(clonedNode);
        }
        this._clonedNodes = clonedNodes;
    }

    clone(obj) {
        let clonedObj = Object.assign({}, obj);
        return clonedObj;
    }

    setInitialState() {
        this.state = 0;
        this.removeTargetIfStateIsZero();
        this.saveCurrentStateAndTarget();
    }

    contractFocus(event) {
        this.setTargetNode(event);
        this.computeState();
        this.computeFocusedData();
        this.setActiveData();
        this.setGravitationalCenter();
        this.applyAutoFit();
    }

    expandFocus() {
        this.decreaseStateIfPossible();
        this.removeTargetIfStateIsZero();
        if (this.shallRefresh()) {
            this.computeFocusedData();
            this.setActiveData();
        }
        this.applyAutoFit();
    }

    attachFunctionToCircles(func) {
        const elementId = this._elementId;
        let circles = d3
            .select("#" + elementId)
            .select("svg")
            .selectAll("circle")._groups[0];
        for (let i = 0; i < circles.length; i++) {
            const circle = circles[i];
            circle.ondblclick = func;
        }
    }

    attachFunctionToLabels(func) {
        const elementId = this._elementId;
        let labels = d3
            .select("#" + elementId)
            .select("svg")
            .selectAll("text")._groups[0];
        for (let i = 0; i < labels.length; i++) {
            const label = labels[i];
            label.ondblclick = func;
        }
    }

    setTargetNode(event) {
        this.saveAsPreviousTarget();
        let node = event.target.__data__;
        this._targetNode = node;
        this._targetType = node.type;
    }

    computeState() {
        if (this.hasTargetChanged() && this.isStateNotZero()) {
            let targetType = this._targetType;
            switch (targetType) {
                case "master":
                    if (this.hasSharedNodes());
                    else this.state = 2;
                    break;
                case "slave":
                    this.state = 1;
                    break;
                default:
                    console.error("Invalid node type");
                    break;
            }
        } else {
            this.increaseStateIfPossible();
        }
    }

    computeFocusedData() {
        this.restoreInputNodes();
        this.selectNodes();
    }

    restoreInputNodes() {
        let clonedNodes = this._clonedNodes;
        let inputNodes = this._inputNodes;
        inputNodes.forEach((node, i) => {
            const clonedNode = clonedNodes[i];
            for (let prop in node) {
                if (this.isNotKinematicProperty(prop)) node[prop] = clonedNode[prop];
            }
        });
    }

    selectNodes() {
        this.initActiveIndices();
        let targetType = this._targetType;
        switch (targetType) {
            case "master":
                this.selectNodesMaster();
                break;
            case "slave":
                this.selectNodesSlave();
                break;
            case "none":
                this.addAllIndices();
                break;
            default:
                console.error("Invalid node type.");
                break;
        }
    }

    selectNodesMaster() {
        const state = this.state;
        switch (state) {
            case 0:
                this.addAllIndices();
                break;
            case 1:
                this.addOwnIndex();
                this.addSlavesIndices();
                this.addSlavesOwnersIndices();
                break;
            case 2:
                this.addOwnIndex();
                this.addSlavesIndices();
                break;
            case 3:
                this.addOwnIndex();
                break;
            default:
                console.error("Invalid case.");
                break;
        }
    }

    selectNodesSlave() {
        const state = this.state;
        switch (state) {
            case 0:
                this.addAllIndices();
                break;
            case 1:
                this.addOwnIndex();
                this.addOwnersIndices();
                break;
            case 2:
                this.addOwnIndex();
                break;
            default:
                console.error("Invalid case.");
                break;
        }
    }

    setActiveData() {
        const activeIndices = this._activeNodeIndices;
        let activeDataManager = this._activeDataManager;
        activeDataManager.compute(activeIndices);
        activeDataManager.updateChart();
    }

    updateField(node, field) {
        let indices = node[field];
        indices = this.updateIndices(indices);
        node[field] = indices;
    }

    updateIndices(rawIndices) {
        let newIndices = [];
        rawIndices.forEach(index => {
            if (this.isNodeActive(index)) newIndices.push(index);
        });
        return newIndices;
    }

    isNodeActive(index) {
        const indices = this._activeNodeIndices;
        const isIndexInActiveList = indices.indexOf(index) !== -1;
        if (isIndexInActiveList) return true;
        else return false;
    }

    initActiveIndices() {
        this._activeNodeIndices = [];
    }

    addOwnIndex() {
        let node = this._targetNode;
        let index = node.number;
        this.addActiveIndices(index);
    }

    addSlavesIndices() {
        let node = this._targetNode;
        let indices = node.slaveNumbers;
        this.addActiveIndices(indices);
    }

    addSlavesOwnersIndices() {
        let indices = this.getSlavesOwnersIndices();
        this.addActiveIndices(indices);
    }

    addOwnersIndices() {
        let node = this._targetNode;
        let indices = node.ownersNumbers;
        this.addActiveIndices(indices);
    }

    addAllIndices() {
        let indices = [];
        let nodes = this._inputNodes;
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            let index = node.number;
            indices.push(index);
        }
        this.addActiveIndices(indices);
    }

    addActiveIndices(indices) {
        let active = this._activeNodeIndices;
        active.selfConcat(indices);
        this._activeNodeIndices = active;
    }

    getSlavesOwnersIndices() {
        let indices = [];
        let node = this._targetNode;
        let targetOwnersNumbers = node.ownersNumbers;
        let slaveNodes = node.slaveNodes;
        slaveNodes.forEach(slaveNode => {
            let ownersNodes = slaveNode.ownersNodes;
            ownersNodes.forEach(ownerNode => {
                let number = ownerNode.number;
                const isNumberNew = indices.indexOf(number) === -1;
                const isThirdParty = targetOwnersNumbers.indexOf(number) === -1;
                if (isNumberNew && isThirdParty) indices.push(number);
            });
        });
        return indices;
    }

    increaseStateIfPossible() {
        if (this.canStateIncrease()) this.increaseState();
        else this.saveCurrentStateAndTarget();
    }

    increaseState() {
        this.state++;
    }

    decreaseStateIfPossible() {
        if (this.canStateDecrease()) this.decreaseState();
        else this.saveCurrentStateAndTarget();
    }

    decreaseState() {
        this.state--;
    }

    saveAsPreviousTarget() {
        let clonedTarget = this.clone(this._targetNode);
        this._previousTargetNode = clonedTarget;
        this._previousTargetType = this._targetType;
    }

    hasTargetChanged() {
        const previousType = this._previousTargetType;
        const currentType = this._targetType;
        let itHas = false;
        const typeHasChanged = currentType !== previousType;
        if (typeHasChanged) itHas = true;
        else {
            if (currentType !== "none") {
                const currentId = this._targetNode.id;
                const previousId = this._previousTargetNode.id;
                const idHasChanged = currentId !== previousId;
                if (idHasChanged) itHas = true;
            }
        }
        return itHas;
    }

    canStateIncrease() {
        const state = this.state;
        const targetType = this._targetType;
        switch (targetType) {
            case "master":
                if (state < 3) return true;
                else return false;
            case "slave":
                if (state < 2) return true;
                else return false;
            default:
                Error("Invalid node type.");
                break;
        }
    }

    hasSharedNodes() {
        let itHas = false;
        const targetNode = this._targetNode;
        const allSharedNumbers = targetNode.sharedNumbers;
        allSharedNumbers.forEach(sharedNumbers => {
            if (sharedNumbers.length > 0) itHas = true;
        });
        return itHas;
    }

    canStateDecrease() {
        let state = this.state;
        if (state > 0) return true;
        else return false;
    }

    isNotKinematicProperty(prop) {
        const kinematicList = ["x", "y", "vx", "vy", "fx", "fy"];
        const isNotInList = kinematicList.indexOf(prop) === -1;
        if (isNotInList) return true;
        else return false;
    }

    isStateNotZero() {
        if (this.state !== 0) return true;
        else return false;
    }

    saveCurrentStateAndTarget() {
        this.state0 = this.state;
        this.saveAsPreviousTarget();
    }

    removeTargetIfStateIsZero() {
        if (this.state === 0) {
            this._targetNode = undefined;
            this._targetType = "none";
        }
    }

    setGravitationalCenter() {
        const nodes = this._chart.getActiveNodes();
        let activeRegion = new ActiveRegion(nodes);
        activeRegion.compute();
        const center = activeRegion.getCenter();
        this._chart.setGravitationalCenter(center);
    }

    applyAutoFit() {
        this._chart.applyAutoFit();
    }

    hasStateChanged() {
        if (this.state !== this.state0) return true;
        else return false;
    }

    shallRefresh() {
        if (this.hasStateChanged() || this.hasTargetChanged()) return true;
        else return false;
    }

    setState(newState) {
        this._state0 = this._state;
        this._state = newState;
    }

    getState() {
        return this._state;
    }

    setPreviousState(state) {
        this._state0 = state;
    }

    getPreviousState() {
        return this._state0;
    }
}
